import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Section } from '../shared';

// Industry Icons (use public images from /public)
const IndustryIcon = ({ type }) => {
  const icons = {
    podcast: '/Use%20Cases.webp',
    music: '/Use%20Cases%20(2).webp',
    gaming: '/Use%20Cases%20(3).webp',
    video: '/Use%20Cases%20(4).webp',
    education: '/Use%20Cases%20(5).webp',
    corporate: '/Use%20Cases%20(6).webp',
  };

  const icon = icons[type] || icons.podcast;
  if (typeof icon === 'string') {
    return <img src={icon} alt={type} className="w-12 h-12 object-contain" />;
  }
  return icon;
};

// Waveform Mockup Component
const WaveformMockup = () => {
  return (
    <div className="bg-dark-base/50 rounded-xl p-4 mt-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 rounded-full bg-neon-orange/20 flex items-center justify-center">
          <svg className="w-4 h-4 text-neon-orange" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
        <div className="flex-1">
          <div className="h-1 bg-neon-orange/20 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-neon-orange rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: '60%' }}
              transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
            />
          </div>
        </div>
        <span className="text-gray-500 text-xs">2:34</span>
      </div>
      <div className="flex items-end justify-center gap-[2px] h-12">
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={i}
            className="w-1 bg-gradient-to-t from-neon-orange/30 to-neon-orange rounded-full"
            animate={{ height: [4, Math.random() * 35 + 5, 4] }}
            transition={{
              duration: 0.8 + Math.random() * 0.4,
              repeat: Infinity,
              delay: i * 0.02,
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Search Bar Component
const SearchBar = ({ value, onChange }) => {
  return (
    <div className="relative max-w-md mx-auto mb-10">
      <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search use cases..."
        className="w-full bg-dark-base border border-neon-orange/20 rounded-full py-3 pl-12 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-neon-orange/50 focus:ring-2 focus:ring-neon-orange/20 transition-all"
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute inset-y-0 right-4 flex items-center text-gray-500 hover:text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

// Accordion Panel Component
const AccordionPanel = ({ useCase, isOpen, onToggle, index, isMobile }) => {
  // On mobile, always show expanded
  const showContent = isMobile || isOpen;

  return (
    <motion.div
      className="mb-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      {/* Header Tab */}
        <motion.button
        onClick={onToggle}
        className={`w-full flex items-center gap-4 p-4 md:p-5 rounded-xl border-2 transition-all ${
          showContent 
            ? 'bg-dark-base border-neon-orange shadow-[0_0_20px_rgba(253,128,46,0.2)]' 
            : 'bg-dark-base/50 border-neon-orange/20 hover:border-neon-orange/40'
        }`}
        whileHover={{ scale: isMobile ? 1 : 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        {/* Icon */}
        <motion.div
          className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
            showContent ? 'bg-neon-orange text-white' : 'bg-neon-orange/10 text-neon-orange'
          }`}
        >
          <IndustryIcon type={useCase.iconType} />
        </motion.div>

        {/* Title */}
        <div className="flex-1 text-left">
          <h3 className="text-lg font-heading font-bold text-white">{useCase.title}</h3>
          <p className="text-gray-500 text-sm">{useCase.subtitle}</p>
        </div>

        {/* Arrow (hidden on mobile) */}
        {!isMobile && (
          <motion.div
            className="text-neon-orange"
            animate={{ rotate: showContent ? 180 : 0 }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </motion.div>
        )}
      </motion.button>

      {/* Content */}
      <AnimatePresence>
        {showContent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-5 md:p-6 bg-dark-deeper/50 border-x-2 border-b-2 border-neon-orange/20 rounded-b-xl mt-[-4px]">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Left: Case Study Text */}
                <div>
                  <h4 className="text-white font-heading font-semibold mb-3">Case Study</h4>
                  <p className="text-gray-400 text-sm mb-4">{useCase.caseStudy}</p>
                  
                  {/* Bullet Points */}
                  <ul className="space-y-2">
                    {useCase.bullets.map((bullet, i) => (
                      <motion.li
                        key={i}
                        className="flex items-start gap-2 text-gray-300 text-sm"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-neon-orange mt-2 flex-shrink-0" />
                        {bullet}
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* Right: Waveform or Image */}
                <div>
                  <h4 className="text-white font-heading font-semibold mb-3">Audio Preview</h4>
                  <WaveformMockup />
                  
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    {useCase.stats.map((stat, i) => (
                      <div key={i} className="bg-dark-base/50 rounded-lg p-3 text-center">
                        <div className="text-neon-orange font-bold text-lg">{stat.value}</div>
                        <div className="text-gray-500 text-xs">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const UseCasesSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openPanel, setOpenPanel] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile on mount and resize
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const useCases = [
    {
      title: "Podcasters & Broadcasters",
      subtitle: "Professional audio for content creators",
      iconType: "podcast",
      caseStudy: "A leading podcast network reduced their post-production time by 70% using our AI-powered noise reduction and auto-leveling features. Episodes that took 4 hours to edit now take just 1 hour.",
      bullets: [
        "Automatic background noise removal",
        "AI-powered voice enhancement",
        "Auto-generate transcripts and chapters",
        "One-click export to all podcast platforms"
      ],
      stats: [
        { value: "70%", label: "Time Saved" },
        { value: "10K+", label: "Podcasters" }
      ]
    },
    {
      title: "Music Producers & Artists",
      subtitle: "AI-powered mixing and mastering",
      iconType: "music",
      caseStudy: "Independent artists are using our stem separation and AI mastering to achieve professional-quality releases without expensive studio time. One artist saved over $5,000 in mastering fees.",
      bullets: [
        "Intelligent stem separation and isolation",
        "AI-assisted mixing suggestions",
        "Professional mastering in seconds",
        "Royalty-free music generation"
      ],
      stats: [
        { value: "$5K", label: "Avg Savings" },
        { value: "50K+", label: "Tracks Processed" }
      ]
    },
    {
      title: "Game Developers",
      subtitle: "Dynamic audio for interactive experiences",
      iconType: "gaming",
      caseStudy: "An indie game studio generated 500+ unique sound effects and ambient tracks for their RPG using our AI generation tools, saving months of sound design work.",
      bullets: [
        "Procedural soundscape generation",
        "Adaptive music that responds to gameplay",
        "Batch process thousands of sound effects",
        "Real-time audio optimization for performance"
      ],
      stats: [
        { value: "500+", label: "SFX Generated" },
        { value: "3mo", label: "Dev Time Saved" }
      ]
    },
    {
      title: "Video & Content Creators",
      subtitle: "Seamless audio for visual content",
      iconType: "video",
      caseStudy: "YouTube creators are using our platform to automatically sync background music, remove unwanted sounds, and enhance voice-overs, leading to better audience retention.",
      bullets: [
        "Auto-sync music to video pacing",
        "Remove wind, traffic, and ambient noise",
        "Voice-over clarity enhancement",
        "Auto-ducking for narration"
      ],
      stats: [
        { value: "40%", label: "Better Retention" },
        { value: "1M+", label: "Videos Enhanced" }
      ]
    },
    {
      title: "E-Learning & Education",
      subtitle: "Clear audio for online learning",
      iconType: "education",
      caseStudy: "An online learning platform improved student completion rates by 25% after implementing our audio enhancement tools to ensure clear, consistent lecture audio.",
      bullets: [
        "Normalize audio across all lessons",
        "Auto-generate captions and subtitles",
        "Remove classroom background noise",
        "Accessibility compliance tools"
      ],
      stats: [
        { value: "25%", label: "Better Completion" },
        { value: "100K+", label: "Hours Enhanced" }
      ]
    },
    {
      title: "Corporate & Enterprise",
      subtitle: "Professional audio at scale",
      iconType: "corporate",
      caseStudy: "A Fortune 500 company standardized their meeting recordings and training materials using our batch processing, ensuring consistent quality across 10,000+ audio files.",
      bullets: [
        "Meeting transcription and summarization",
        "Brand-consistent audio processing",
        "Secure enterprise deployment options",
        "API integration for existing workflows"
      ],
      stats: [
        { value: "10K+", label: "Files Processed" },
        { value: "99.9%", label: "Uptime SLA" }
      ]
    },
  ];

  // Filter use cases based on search
  const filteredUseCases = useMemo(() => {
    if (!searchQuery.trim()) return useCases;
    
    const query = searchQuery.toLowerCase();
    return useCases.filter(useCase => 
      useCase.title.toLowerCase().includes(query) ||
      useCase.subtitle.toLowerCase().includes(query) ||
      useCase.caseStudy.toLowerCase().includes(query) ||
      useCase.bullets.some(bullet => bullet.toLowerCase().includes(query))
    );
  }, [searchQuery]);

  return (
    <Section id="use-cases" className="bg-dark-deeper relative overflow-hidden">
      <div className="max-w-4xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10">
          <motion.span 
            className="text-neon-orange font-semibold text-sm uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Use Cases & Industries
          </motion.span>
          
          <motion.h2 
            className="text-3xl md:text-4xl font-heading font-bold text-white mt-4 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Built for{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange to-neon-orange-light">
              Every Creator
            </span>
          </motion.h2>
        </div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <SearchBar value={searchQuery} onChange={setSearchQuery} />
        </motion.div>

        {/* Results count */}
        {searchQuery && (
          <motion.p 
            className="text-gray-500 text-sm text-center mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Found {filteredUseCases.length} result{filteredUseCases.length !== 1 ? 's' : ''} for "{searchQuery}"
          </motion.p>
        )}

        {/* Accordion Panels */}
        <div>
          {filteredUseCases.length > 0 ? (
            filteredUseCases.map((useCase, i) => (
              <AccordionPanel
                key={useCase.title}
                useCase={useCase}
                index={i}
                isOpen={openPanel === i}
                onToggle={() => setOpenPanel(openPanel === i ? -1 : i)}
                isMobile={isMobile}
              />
            ))
          ) : (
            <motion.div 
              className="text-center py-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="text-gray-500 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-400">No use cases found for "{searchQuery}"</p>
              <button 
                onClick={() => setSearchQuery('')}
                className="text-neon-orange mt-2 hover:underline"
              >
                Clear search
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default UseCasesSection;
